import { inject, Injectable } from '@angular/core';
import './google-tag.interfaces';
import { defer, from, ReplaySubject, shareReplay, switchMap } from 'rxjs';
import {
  DataLayerEvent,
  DataLayerMessage,
  GTM_CONFIG,
} from './google-tag.interfaces';
import { TrackingConsentStatus } from '../tracking-consent-banner/tracking-consent.interfaces';
import { TrackingService } from '../tracking.service';

@Injectable()
export class GoogleTagManagerService {
  /**
   * Subject to send data layer messages into
   * @private
   */
  private dataLayerSubject = new ReplaySubject<DataLayerMessage>();

  /**
   * Singleton pipe that replays all data layer messages to subscribers,
   * initializes the data layer when the first subscriber subscribes.
   */
  public dataLayer$ = defer(() => from(this.initialize())).pipe(
    shareReplay(1),
    switchMap(() => this.dataLayerSubject.asObservable()),
  );

  private readonly config = inject(GTM_CONFIG);
  private readonly trackingService = inject(TrackingService);

  private get window() {
    return window;
  }

  private get document() {
    return document;
  }

  private get dataLayer() {
    return (this.window.dataLayer ??= []);
  }

  private initialize() {
    this.push({
      'gtm.start': new Date().getTime(),
      event: DataLayerEvent.Initialization,
    });

    // Check if we already have tracking consent
    if (
      this.trackingService.getTrackingConsent() ===
      TrackingConsentStatus.Granted
    ) {
      // Send analytics consent.
      this.push({ event: DataLayerEvent.AnalyticsConsent });
      // Then send the (missed) page event of the page we are currently on
      this.push({ event: DataLayerEvent.HistoryChanged });
    }

    const gtmScript = this.document.createElement('script');
    gtmScript.async = true;
    gtmScript.src = this.getGoogleTagManagerUrl().href;
    const head = this.document.getElementsByTagName('head')[0];
    head.insertBefore(gtmScript, head.children[0]);
    return new Promise((resolve, reject) => {
      gtmScript.addEventListener('load', () => {
        return resolve(true);
      });
      gtmScript.addEventListener('error', (err) => {
        return reject(err.error ?? new Error('Unknown error event in GTM.'));
      });
    });
  }

  private getGoogleTagManagerUrl() {
    const url = new URL('https://www.googletagmanager.com/gtm.js');
    url.searchParams.set('id', this.config.containerId);
    if (this.config.authentication)
      url.searchParams.set('gtm_auth', this.config.authentication);
    if (this.config.preview)
      url.searchParams.set('gtm_preview', this.config.preview);
    if (this.config.cookies)
      url.searchParams.set('gtm_cookies_win', this.config.cookies);

    return url;
  }

  private push(message: DataLayerMessage) {
    this.dataLayer.push(message);
    this.dataLayerSubject.next(message);
  }
}
